import "./style.scss";

import type { BasicTableColumn } from "components/shared/BasicTable";
import BasicTable from "components/shared/BasicTable";
import ChannelIcon from "components/shared/ChannelIcon";
import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import type { TFormCreateTitle } from "components/TitleFormContainer/types";
import { getCurrencySymbol } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useMemo } from "react";
import type { FieldArrayWithId } from "react-hook-form";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { CalculationBaseColumn } from "store/calculationBaseColumnLabelsStore";
import { getCalculationBaseColumnLabels } from "store/calculationBaseColumnLabelsStore";
import { useAppDispatch, useAppSelector } from "store/store";

/**
 * Opening cumulative sales per channel as an editable table.
 * Since the calculation base is chosen on the contract (not the channel), quantity and turnover are
 * always captured independently, plus any custom turnover column the company has configured.
 */
interface ICumulativeSalesArray {
    /**
     * If title exist on an archive period
     */
    hasArchive?: boolean;
}

type TChannelRow = FieldArrayWithId<TFormCreateTitle, "startingChannelsData", "id">;

type TEditableColumn = {
    field: Extract<CalculationBaseColumn, "quantity" | "turnover" | "turnover_1" | "turnover_2">;
    label: string;
    /** Monetary columns are stored in cents and edited as floats. */
    isMonetary: boolean;
};

const CUSTOM_TURNOVER_COLUMNS: Extract<CalculationBaseColumn, "turnover_1" | "turnover_2">[] = [
    "turnover_1",
    "turnover_2",
];

const CumulativeSalesArray = ({ hasArchive }: ICumulativeSalesArray) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const {
        formState: { errors },
        control,
    } = useFormContext<TFormCreateTitle>();
    const { fields } = useFieldArray({
        control,
        name: "startingChannelsData",
    });

    const { labels } = useAppSelector((state) => state.calculationBaseColumnLabels);
    const { companyInfos } = useCompanySettings();
    const currencySymbol = getCurrencySymbol(companyInfos?.currency ?? "EUR");

    // Always refresh on mount so freshly configured turnover columns show up even if another screen
    // previously cached an empty/stale list (status would no longer be "idle").
    useEffect(() => {
        dispatch(getCalculationBaseColumnLabels());
    }, [dispatch]);

    const labelByColumn = useMemo(
        () =>
            Object.fromEntries(labels.map((label) => [label.calculationBaseColumn, label.label])) as Partial<
                Record<CalculationBaseColumn, string>
            >,
        [labels],
    );

    const resolveColumnLabel = (column: CalculationBaseColumn): string =>
        labelByColumn[column] ?? t(`CalculationBaseColumnLabels.Columns.${column}`);

    // quantity + turnover always; custom turnover columns only when the company configured a label.
    const editableColumns = useMemo((): TEditableColumn[] => {
        const alwaysVisible: TEditableColumn[] = [
            { field: "quantity", label: resolveColumnLabel("quantity"), isMonetary: false },
            { field: "turnover", label: resolveColumnLabel("turnover"), isMonetary: true },
        ];
        const custom = CUSTOM_TURNOVER_COLUMNS.flatMap((column) =>
            labelByColumn[column] ? [{ field: column, label: labelByColumn[column] as string, isMonetary: true }] : [],
        );
        return [...alwaysVisible, ...custom];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [labelByColumn, t]);

    const archiveDisabledText = t("Titles.HasArchivesNonEditableData");

    const columns: BasicTableColumn<TChannelRow>[] = useMemo(() => {
        const channelColumn: BasicTableColumn<TChannelRow> = {
            header: t("Channels.Channel"),
            cellClassName: "cumulative-sales-channel-cell",
            render: (channel) => (
                <div className="cumulative-sales-channel">
                    <span className="cumulative-sales-channel-icon">
                        <ChannelIcon channelKind={channel.kind as number} width="14px" height="14px" />
                    </span>
                    <span className="cumulative-sales-channel-name">{channel.name}</span>
                </div>
            ),
        };

        const valueColumns: BasicTableColumn<TChannelRow>[] = editableColumns.map(({ field, label, isMonetary }) => ({
            header: isMonetary ? `${label} ${currencySymbol}` : label,
            align: "right",
            cellClassName: "cumulative-sales-value-cell",
            render: (channel, index) => (
                <NumberFormComponent
                    control={control}
                    acceptsNegative
                    id={`channel-${channel.channelId}-${field}`}
                    label=""
                    placeholder="0"
                    controlName={`startingChannelsData.${index}.${field}`}
                    disabled={hasArchive}
                    disabledText={archiveDisabledText}
                    hideDisabledLockIcon={hasArchive}
                    error={errors.startingChannelsData?.[index]?.[field]}
                    float={isMonetary}
                />
            ),
        }));

        return [channelColumn, ...valueColumns];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editableColumns, control, hasArchive, errors, currencySymbol, archiveDisabledText, t]);

    if (!fields.length) return null;

    return (
        <div className="form-cumulative-sales-table">
            <BasicTable columns={columns} rows={fields} />
        </div>
    );
};

export default CumulativeSalesArray;
